import { TrendingUp, TrendingDown, DollarSign, Percent } from 'lucide-react';
import { Holding } from '../api/portfolio';

interface PerformanceSummaryProps {
  portfolio: Holding[];
}

export default function PerformanceSummary({ portfolio }: PerformanceSummaryProps) {
  // Calculate totals
  const totalValue = portfolio.reduce((sum, h) => sum + (h.totalValue || 0), 0);
  const totalCostBasis = portfolio.reduce((sum, h) => sum + (h.costBasis || 0), 0);
  const totalGainLoss = totalValue - totalCostBasis;
  const totalGainLossPercent = totalCostBasis > 0 ? (totalGainLoss / totalCostBasis) * 100 : 0;
  
  // Count holdings with cost basis
  const trackedHoldings = portfolio.filter(h => h.purchase_price).length;
  const untrackedHoldings = portfolio.length - trackedHoldings;
  
  // Calculate winners and losers
  const winners = portfolio.filter(h => h.gainLoss && h.gainLoss > 0).length;
  const losers = portfolio.filter(h => h.gainLoss && h.gainLoss < 0).length;

  if (trackedHoldings === 0) {
    return (
      <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
            <TrendingUp className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Performance Tracking</h3>
            <p className="text-sm text-gray-600">Track your gains and losses</p>
          </div>
        </div>
        <p className="text-sm text-gray-700 mb-3">
          Add purchase prices to your holdings to track performance over time!
        </p>
        <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-100 px-3 py-2 rounded-lg">
          <span>💡</span>
          <span>Edit existing holdings or add new ones with purchase price to get started</span>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-primary-100 to-blue-100 rounded-lg">
            {totalGainLoss >= 0 ? (
              <TrendingUp className="w-6 h-6 text-green-600" />
            ) : (
              <TrendingDown className="w-6 h-6 text-red-600" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Portfolio Performance</h3>
            <p className="text-xs text-gray-500">{trackedHoldings} of {portfolio.length} holdings tracked</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Total Gain/Loss */}
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-gray-500" />
            <p className="text-xs font-semibold text-gray-500 uppercase">Total Gain/Loss</p>
          </div>
          <p className={`text-2xl font-bold ${totalGainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {totalGainLoss >= 0 ? '+' : ''}${totalGainLoss.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </p>
        </div>

        {/* Percentage Return */}
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-2">
            <Percent className="w-4 h-4 text-gray-500" />
            <p className="text-xs font-semibold text-gray-500 uppercase">Return</p>
          </div>
          <p className={`text-2xl font-bold ${totalGainLossPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {totalGainLossPercent >= 0 ? '+' : ''}{totalGainLossPercent.toFixed(2)}%
          </p>
        </div>

        {/* Winners */}
        <div className="stat-card bg-green-50 border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-green-600" />
            <p className="text-xs font-semibold text-green-700 uppercase">Winners</p>
          </div>
          <p className="text-2xl font-bold text-green-600">{winners}</p>
          <p className="text-xs text-green-600 mt-1">Profitable positions</p>
        </div>

        {/* Losers */}
        <div className="stat-card bg-red-50 border-red-200">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-4 h-4 text-red-600" />
            <p className="text-xs font-semibold text-red-700 uppercase">Losers</p>
          </div>
          <p className="text-2xl font-bold text-red-600">{losers}</p>
          <p className="text-xs text-red-600 mt-1">Losing positions</p>
        </div>
      </div>

      {/* Cost Basis Info */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Total Cost Basis</p>
            <p className="font-semibold text-gray-900">${totalCostBasis.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
          </div>
          <div>
            <p className="text-gray-600">Current Value</p>
            <p className="font-semibold text-gray-900">${totalValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
          </div>
        </div>
      </div>

      {untrackedHoldings > 0 && (
        <div className="mt-3 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg border border-amber-200">
          <span>⚠️</span>
          <span>{untrackedHoldings} holding{untrackedHoldings > 1 ? 's' : ''} without purchase price - add it to track full performance</span>
        </div>
      )}
    </div>
  );
}
